import { HTMLAttributes, forwardRef } from 'react';
import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';

type AlertType = 'info' | 'success' | 'warning' | 'error';

interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  type?: AlertType;
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const alertStyles: Record<AlertType, { container: string; icon: string }> = {
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-800',
    icon: 'text-blue-500',
  },
  success: {
    container: 'bg-green-50 border-green-200 text-green-800',
    icon: 'text-green-500',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 text-amber-800',
    icon: 'text-amber-500',
  },
  error: {
    container: 'bg-red-50 border-red-200 text-red-800',
    icon: 'text-red-500',
  },
};

const alertIcons: Record<AlertType, React.ReactNode> = {
  info: <Info className="w-5 h-5" />,
  success: <CheckCircle className="w-5 h-5" />,
  warning: <AlertCircle className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ type = 'info', title, dismissible, onDismiss, className = '', children, ...props }, ref) => {
    const styles = alertStyles[type];

    return (
      <div
        ref={ref}
        role="alert"
        className={`
          flex items-start gap-3 p-4
          border rounded-lg
          ${styles.container}
          ${className}
        `}
        {...props}
      >
        <span className={`flex-shrink-0 ${styles.icon}`}>{alertIcons[type]}</span>
        <div className="flex-1 min-w-0">
          {title && <h4 className="font-medium mb-1">{title}</h4>}
          <div className="text-sm opacity-90">{children}</div>
        </div>
        {dismissible && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';
